import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpErrorResponse, HttpRequest, HttpHandler, HttpEvent, HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError, defer, EMPTY, firstValueFrom } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { TokenService } from './token.service';
import { StandaloneAuthService } from './standalone-auth.service';
import { STORAGE_KEYS, LEGACY_STORAGE_KEYS } from '@shared/constants';


/**
 * Trata erros 401: enfileira a requisição, renova o token via refresh_token OAuth2
 * (StandaloneAuthService.refreshWithToken()) e refaz a fila — evitando renovações
 * simultâneas com a flag isRefreshingToken.
 */
@Injectable()
export class AuthErrorInterceptorService implements HttpInterceptor {
  private isRefreshingToken = false;
  private retryQueue: Array<{
    request: HttpRequest<unknown>;
    next: HttpHandler;
    resolve: (value: Observable<HttpEvent<unknown>>) => void;
    reject: (reason?: unknown) => void;
  }> = [];
  // Cliente HTTP que bypassa todos os interceptors (usado em refreshUserAccess).
  // HttpBackend envia diretamente para o XHR sem passar pela cadeia de interceptors,
  // evitando loop de retry quando @me/access é chamado dentro do próprio interceptor.
  private httpNative: HttpClient;

  constructor(
    private tokenService: TokenService,
    private standaloneAuth: StandaloneAuthService,
    httpBackend: HttpBackend
  ) {
    this.httpNative = new HttpClient(httpBackend);
  }

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        const retryCount = parseInt(req.headers.get('X-Auth-Retry-Count') || '0', 10);

        // Status 0 (CORS) num RETRY de um request que já tomou 401 no original significa
        // que o servidor fez 302 pra tela de login (sessão expirada por inatividade, mesmo
        // com JWT ainda válido) — não é CORS legítimo, sempre redireciona.
        if (error.status === 0 && retryCount >= 1) {
          this.redirectToLoginSilently();
          return EMPTY;
        }

        if (error.status === 403) {
          window.dispatchEvent(new CustomEvent('abrirModalSemPermissao'));
          return EMPTY;
        }

        if (error.status !== 401) {
          return throwError(() => error);
        }

        if (retryCount >= 1) {
          // Já retentou uma vez — token definitivamente inválido, ir para login.
          this.redirectToLoginSilently();
          return EMPTY;
        }

        return this.addRequestRetry(req, next);
      })
    );
  }

  /** Detecta 401 explicitamente de token expirado (servidor com timeout de sessão < JWT exp). */
  private isServerTokenExpiredError(error: HttpErrorResponse): boolean {
    try {
      const EXPIRY_KEYWORDS = ['expirado', 'expired', 'token inválido', 'invalid token'];

      if (error.error && typeof error.error === 'object') {
        const msg = String(error.error.message || '').toLowerCase();
        if (EXPIRY_KEYWORDS.some(kw => msg.includes(kw))) return true;
      }

      if (typeof error.error === 'string') {
        const body = error.error.toLowerCase();
        if (EXPIRY_KEYWORDS.some(kw => body.includes(kw))) return true;
      }

      const angularMsg = String(error.message || '').toLowerCase();
      if (EXPIRY_KEYWORDS.some(kw => angularMsg.includes(kw))) return true;

      return false;
    } catch {
      return false;
    }
  }

  private addRequestRetry(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    return defer(() => {
      return new Promise<Observable<HttpEvent<unknown>>>((resolve, reject) => {
        this.retryQueue.push({
          request,
          next,
          resolve,
          reject
        });

        if (!this.isRefreshingToken) {
          this.startRefreshing();

          // Tenta refresh_token OAuth2 primeiro; sem ele (fluxo /?pwa_auth=true não gera
          // refresh_token), cai pro bridge token e deixa o catchError de retryAllRequests()
          // redirecionar se o servidor rejeitar.
          this.standaloneAuth.refreshWithToken()
            .then(async (newToken) => {
              // Fallback de leitura na chave antiga (__pwa_bridge_token) por 1 release — TODO remover
              const tokenToUse = newToken || localStorage.getItem(STORAGE_KEYS.BRIDGE_TOKEN) ||
                localStorage.getItem(LEGACY_STORAGE_KEYS.BRIDGE_TOKEN);
              if (!tokenToUse) {
                this.redirectToLoginSilently();
                return;
              }
              // Garante licenseId fresco pro retry (não só no startup/modal)
              await this.refreshUserAccess(tokenToUse);
              this.retryAllRequests();
              this.stopRefreshing();
            })
            .catch(() => {
              this.redirectToLoginSilently();
            });
        }
      });
    }).pipe(
      switchMap((obs) => obs)
    );
  }

  /**
   * `next.handle(retryReq)` não passa pelo interceptor de novo — por isso 401/CORS do
   * próprio retry são capturados aqui, senão o erro vaza pro componente.
   */
  private retryAllRequests(): void {
    this.retryQueue.forEach(({ request, next, resolve }) => {
      const newToken = this.getLatestToken();

      if (!newToken) {
        this.redirectToLoginSilently();
        resolve(EMPTY);
        return;
      }

      const retryReq = this.addTokenHeader(request, newToken);

      // Status 0 = servidor fez 302 pra login (sessão expirada, não CORS legítimo, já que
      // o request original teve 401 real). 401 só redireciona se o servidor confirmar
      // token expirado — 401 de acesso negado não deve, senão causa loop em fresh login.
      const retryObs = next.handle(retryReq).pipe(
        catchError((retryError: HttpErrorResponse) => {
          if (retryError.status === 0) {
            this.redirectToLoginSilently();
            return EMPTY;
          }
          if (retryError.status === 401 && this.isServerTokenExpiredError(retryError)) {
            this.redirectToLoginSilently();
            return EMPTY;
          }
          return throwError(() => retryError);
        })
      );

      resolve(retryObs);
    });

    // Limpa a fila
    this.retryQueue = [];
  }

  /**
   * Em modo TWA/standalone com token inválido: limpa tokens, resolve todos os
   * requests pendentes com EMPTY (sem exibir erro) e redireciona para login.
   */
  private redirectToLoginSilently(): void {

    // Limpar tokens de todas as fontes reais deste domínio para evitar reaproveitamento
    // de sessão stale no próximo login. Chave antiga (__pwa_bridge_*) limpa junto por
    // 1 release — TODO remover quando o fallback de leitura for removido.
    localStorage.removeItem(STORAGE_KEYS.BRIDGE_TOKEN);
    localStorage.removeItem(LEGACY_STORAGE_KEYS.BRIDGE_TOKEN);
    localStorage.removeItem('__pwa_standalone_token');
    localStorage.removeItem('__pwa_standalone_token_expiry');

    // Sem limpar: novo token + User-Access stale da sessão anterior → timeout → 504
    // na plataforma de autorização.
    localStorage.removeItem(STORAGE_KEYS.BRIDGE_USER_ACCESS);
    localStorage.removeItem(LEGACY_STORAGE_KEYS.BRIDGE_USER_ACCESS);
    localStorage.removeItem(STORAGE_KEYS.BRIDGE_USER_ACCESS_CONTEXT);
    localStorage.removeItem(LEGACY_STORAGE_KEYS.BRIDGE_USER_ACCESS_CONTEXT);
    localStorage.removeItem(STORAGE_KEYS.BRIDGE_ACCESS_PAIR);
    localStorage.removeItem(LEGACY_STORAGE_KEYS.BRIDGE_ACCESS_PAIR);
    localStorage.removeItem(STORAGE_KEYS.BRIDGE_APP_CONTEXT);
    localStorage.removeItem(LEGACY_STORAGE_KEYS.BRIDGE_APP_CONTEXT);
    // Sem limpar: MobileContextService reusaria o contexto antigo com o novo token,
    // e o backend rejeitaria por User-Access/app-context não baterem → "Acesso Negado".
    localStorage.removeItem(STORAGE_KEYS.BRIDGE_CONTEXTO_COMPLETO);
    localStorage.removeItem(LEGACY_STORAGE_KEYS.BRIDGE_CONTEXTO_COMPLETO);

    this.retryQueue.forEach(({ resolve }) => resolve(EMPTY));
    this.retryQueue = [];
    this.stopRefreshing();

    // Overlay sólido evita flash do conteúdo do PWA durante a transição para o login
    const overlay = document.createElement('div');
    overlay.style.cssText = 'position:fixed;top:0;left:0;right:0;bottom:0;width:100vw;height:100vh;z-index:99999;background:var(--bg-secondary, #f5f7fa)';
    document.body.appendChild(overlay);

    setTimeout(() => this.performLoginRedirect(), 50);
  }

  /** Isolado num método próprio para poder ser substituído em teste. */
  private performLoginRedirect(): void {
    window.location.href = '/?pwa_auth=true';
  }

  private startRefreshing(): void {
    this.isRefreshingToken = true;
  }

  private stopRefreshing(): void {
    this.isRefreshingToken = false;
  }

  /** PRIORIDADE: localStorage (ponte) > outras fontes. */
  private getLatestToken(): string | null {
    const bridgeToken = localStorage.getItem(STORAGE_KEYS.BRIDGE_TOKEN) ||
      localStorage.getItem(LEGACY_STORAGE_KEYS.BRIDGE_TOKEN);
    if (bridgeToken) {
      return bridgeToken;
    }

    return this.tokenService.getAccessToken();
  }

  /**
   * Atualiza __pwa_user_access via @me/access usando o token fornecido.
   * Usa HttpBackend (bypassa todos os interceptors) para evitar loop de retry.
   * Falha silenciosamente — retryAllRequests() é chamado independente do resultado.
   */
  private async refreshUserAccess(token: string): Promise<void> {
    try {
      const hostname = window.location.hostname;
      const base = (hostname.includes('localhost') || hostname.includes('127.0.0.1') || hostname.includes('test.betha.cloud'))
        ? 'https://plataforma-autorizacoes.test.betha.cloud'
        : 'https://plataforma-autorizacoes.betha.cloud';
      const url = `${base}/user-accounts/v0.1/api/suite/users/@me/access`;

      // Fallback de leitura na chave antiga (__pwa_bridge_*) por 1 release — TODO remover
      let entityId: string | null = null;
      const savedCtx = localStorage.getItem(STORAGE_KEYS.BRIDGE_CONTEXTO_COMPLETO) ||
        localStorage.getItem(LEGACY_STORAGE_KEYS.BRIDGE_CONTEXTO_COMPLETO);
      if (savedCtx) {
        const ctx = JSON.parse(savedCtx);
        entityId = ctx?.entidade?.id ? String(ctx.entidade.id) : null;
      }
      if (!entityId) {
        // Sem entityId mas com _userAccess já salvo da URL (licenseId correto): manter e não
        // sobrescrever com dado stale de uma busca sem entityId.
        const savedUserAccess = localStorage.getItem(STORAGE_KEYS.BRIDGE_USER_ACCESS) ||
          localStorage.getItem(LEGACY_STORAGE_KEYS.BRIDGE_USER_ACCESS);
        if (savedUserAccess) {
          console.log('[AuthErrorInterceptor] ℹ️ entityId não disponível mas _userAccess já salvo da URL — mantendo:', savedUserAccess.substring(0, 15) + '...');
          return;
        }
      }

      const acessos = await firstValueFrom(
        this.httpNative.get<any[]>(url, {
          headers: new HttpHeaders({ 'Authorization': `Bearer ${token}` })
        })
      );

      if (acessos && entityId) {
        const acesso = acessos.find((a: any) =>
          String(a.values?.entity) === entityId &&
          !a.values?.databaseId &&
          a.licenseId
        );
        if (acesso?.licenseId) {
          localStorage.setItem(STORAGE_KEYS.BRIDGE_USER_ACCESS, acesso.licenseId);
          localStorage.setItem(
            STORAGE_KEYS.BRIDGE_USER_ACCESS_CONTEXT,
            JSON.stringify({
              licenseId: acesso.licenseId,
              accessId: acesso.id,
              admin: Boolean((acesso as any).admin ?? (acesso as any).values?.admin)
            })
          );
          if (acesso.id) {
            localStorage.setItem(
              STORAGE_KEYS.BRIDGE_ACCESS_PAIR,
              JSON.stringify({ licenseId: acesso.licenseId, accessId: acesso.id })
            );
          }
        }
      }
    } catch (e) {
      // Busca de acesso/licença é best-effort — falha aqui não deve interromper o fluxo
    }
  }

  /** Também adiciona app-context e user-access, igual ao AuthInterceptor. */
  private addTokenHeader(request: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`
    };

    // Fallback de leitura na chave antiga (__pwa_bridge_app_context) por 1 release — TODO remover
    const appContext = localStorage.getItem(STORAGE_KEYS.BRIDGE_APP_CONTEXT) ||
      localStorage.getItem(LEGACY_STORAGE_KEYS.BRIDGE_APP_CONTEXT);

    if (appContext) {
      headers['app-context'] = appContext;
    }

    // Fallback de leitura na chave antiga (__pwa_bridge_user_access) por 1 release — TODO remover
    const userAccess = localStorage.getItem(STORAGE_KEYS.BRIDGE_USER_ACCESS) ||
      localStorage.getItem(LEGACY_STORAGE_KEYS.BRIDGE_USER_ACCESS);

    // Endpoints de infraestrutura da plataforma Betha NÃO aceitam User-Access
    // (qualquer valor, válido ou stale, causa 504 Gateway Timeout).
    const isPlatformEndpoint = request.url.includes('plataforma-autorizacoes') ||
                               request.url.includes('login-session-verifier') ||
                               request.url.includes('my-preferences.educacao');

    if (userAccess && !isPlatformEndpoint) {
      // 'User-Access' maiúscula pra corresponder ao AngularJS (contexto-estabelecimento.service.js)
      headers['User-Access'] = userAccess;
    }

    const currentRetryCount = parseInt(request.headers.get('X-Auth-Retry-Count') || '0', 10);
    headers['X-Auth-Retry-Count'] = String(currentRetryCount + 1);

    return request.clone({ setHeaders: headers });
  }
}
